import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/features/identity/composables/useAuthStore";
import { isIdLinkQuery } from "@/utils/idLink";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    component: () => import("@/pages/RootPage.vue"),
    meta: { permission: "Aas.View" },
  },
  {
    path: "/aasx/:aasIdentifier",
    name: "aas-view",
    component: () => import("@/pages/aas/AasViewPage.vue"),
    meta: { permission: "Aas.View" },
  },
  {
    path: "/aasx/detail/:aasIdentifier",
    name: "aas-detail-view",
    component: () => import("@/pages/aas/AasDetailViewPage.vue"),
    meta: { permission: "Aas.View" },
  },
  {
    path: "/aasx/edit/:aasIdentifier",
    name: "aas-edit",
    component: () => import("@/pages/aas/AasEditPage.vue"),
    meta: { permission: "Aas.Edit" },
  },
  {
    path: "/templates/submodels",
    name: "submodel-templates",
    component: () => import("@/pages/submodel-templates/SubmodelTemplateListPage.vue"),
    meta: { permission: "Template.View" },
  },
  {
    path: "/templates/submodel/:submodelIdentifier",
    name: "submodel-template-detail",
    component: () => import("@/pages/submodel-templates/SubmodelTemplateDetailPage.vue"),
    meta: { permission: "Template.View" },
  },
  {
    path: "/templates/submodel/edit/:submodelIdentifier",
    name: "submodel-template-edit",
    component: () => import("@/pages/submodel-templates/SubmodelTemplateEditPage.vue"),
    meta: { permission: "Template.Upload" },
  },
  {
    path: "/history/:aasIdentifier",
    name: "aas-history",
    component: () => import("@/pages/aas/AasHistoryPage.vue"),
    meta: { permission: "Aas.History" },
  },
  {
    path: "/history/view/:aasIdentifier",
    name: "aas-history-detail",
    component: () => import("@/pages/aas/AasHistoryDetailPage.vue"),
    meta: { permission: "Aas.History" },
  },
  {
    path: "/submodel/history/:submodelIdentifier",
    name: "submodel-template-history",
    component: () => import("@/pages/submodel-templates/SubmodelTemplateHistoryPage.vue"),
    meta: { permission: "Template.View" },
  },
  {
    path: "/submodel/history/view/:submodelIdentifier",
    name: "submodel-template-history-detail",
    component: () => import("@/pages/submodel-templates/SubmodelTemplateHistoryDetailPage.vue"),
    meta: { permission: "Template.View" },
  },
  {
    path: "/users",
    name: "users",
    component: () => import("@/pages/identity/UsersPage.vue"),
    meta: { permission: "Users.Administration" },
  },
  {
    path: "/users/edit/:userId",
    name: "user-edit",
    component: () => import("@/pages/identity/UserEditPage.vue"),
    meta: { permission: "Users.Administration" },
  },
  {
    path: "/roles",
    name: "roles",
    component: () => import("@/pages/identity/RolesPage.vue"),
    meta: { permission: "Roles.Administration" },
  },
  {
    path: "/roles/create",
    name: "role-create",
    component: () => import("@/pages/identity/RoleCreatePage.vue"),
    meta: { permission: "Roles.Administration" },
  },
  {
    path: "/roles/edit/:id",
    name: "role-edit",
    component: () => import("@/pages/identity/RoleEditPage.vue"),
    meta: { permission: "Roles.Administration" },
  },
  {
    path: "/classifications",
    name: "classifications",
    component: () => import("@/pages/classifications/ClassificationsPage.vue"),
    meta: { permission: "Classifications.Administration" },
  },
  {
    path: "/classifications/create",
    name: "classification-create",
    component: () => import("@/pages/classifications/ClassificationCreatePage.vue"),
    meta: { permission: "Classifications.Administration" },
  },
  {
    path: "/classifications/edit/:id",
    name: "classification-edit",
    component: () => import("@/pages/classifications/ClassificationEditPage.vue"),
    meta: { permission: "Classifications.Administration" },
  },
  // Legacy URL redirects (used by UI tests and old bookmarks)
  { path: "/Home/Index", redirect: "/" },
  { path: "/SubmodelTemplate/Index", redirect: "/templates/submodels" },
  {
    path: "/thanks",
    name: "thanks",
    component: () => import("@/pages/system/ThanksPage.vue"),
  },
  {
    path: "/accessdenied",
    name: "access-denied",
    component: () => import("@/pages/system/AccessDeniedPage.vue"),
  },
  {
    path: "/:idShort",
    name: "aas-public-view",
    component: () => import("@/pages/aas/AasDetailViewPage.vue"),
    meta: { allowAnonymous: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const reservedRootPaths = new Set([
  "",
  "aasx",
  "templates",
  "history",
  "submodel",
  "users",
  "roles",
  "classifications",
  "home",
  "submodeltemplate",
  "signin",
  "signout",
  "identity",
  "api",
  "swagger",
  "accessdenied",
  "thanks",
]);

function isPublicIdShortPath(path: string): boolean {
  const normalized = path.trim().replace(/^\/+|\/+$/g, "");
  if (!normalized || normalized.includes("/")) {
    return false;
  }

  return !reservedRootPaths.has(normalized.toLowerCase());
}

function isAnonymousRoute(to: { name?: unknown; meta: Record<string, unknown> }): boolean {
  return to.name === "aas-public-view"
    || (to.meta.allowAnonymous as boolean | undefined) === true;
}

router.beforeEach(async (to) => {
  const isPublicIdLink = to.name === "home" && isIdLinkQuery(to.query);
  if (isAnonymousRoute(to) || isPublicIdShortPath(to.path) || isPublicIdLink) {
    return true;
  }

  const { state: authState, waitForAuth, hasPermission } = useAuthStore();
  await waitForAuth();

  const permission = to.meta.permission as string | undefined;
  if (!permission) {
    return true;
  }

  if (!authState.value.userId) {
    window.location.href = `/signin?returnUrl=${encodeURIComponent(to.fullPath)}`;
    return false;
  }

  if (!hasPermission(permission)) {
    return { name: "access-denied" };
  }

  return true;
});

export default router;
